<template>
	<nk-wrapper v-if="context && typeof context === 'object'" spacing="md">
		<nk-row>
			<nk-col v-if="context.datum_von" cols="shrink" class="hidden xl:block">
				<nk-date
					v-if="$moment().isAfter(context.datum_von)"
					:date="$moment().format()"
				/>
				<nk-date v-else :date="context.datum_von" />
			</nk-col>
			<nk-col cols="full" sm="auto">
				<nk-title max-lines="2" size="2xl" tag="h1">
					{{ context.titel }}
				</nk-title>
				<nk-image
					v-if="context.bild"
					class="md:ml-4 md:mb-4 md:float-right w-full md:max-w-xs xl:max-w-sm"
					:copyright="context.bildcopyright"
					:src="context.bild"
					:alt="context.bildtext"
					:caption="context.bildtext"
					:lightbox="hasImageGallery ? 'bildergalerie' : true"
					:gallery="
						hasImageGallery ? context.bildergalerie_zuordnung.bilder : []
					"
				/>
				<nk-wrapper spacing="md">
					<nk-list variant="clean">
						<nk-list-item
							v-if="
								getDateTimeString({
									dateFrom: context.datum_von,
									dateTo: context.datum_bis,
								})
							"
							icon="event"
						>
							{{
								getDateTimeString({
									dateFrom: context.datum_von,
									dateTo: context.datum_bis,
								})
							}}
						</nk-list-item>
						<nk-list-item
							v-if="
								getDateTimeString({
									timeFrom: context.datum_von,
									timeTo: context.datum_bis,
								})
							"
							icon="time"
						>
							{{
								getDateTimeString({
									timeFrom: context.datum_von,
									timeTo: context.datum_bis,
								})
							}}
						</nk-list-item>
						<nk-list-item
							v-if="context.brancheneintrag_veranstalter"
							icon="user"
						>
							<nk-link
								:link="
									context.brancheneintrag_veranstalter.rubriken[0].detailURL
								"
							>
								{{ context.brancheneintrag_veranstalter.titel }}
							</nk-link>
						</nk-list-item>
						<nk-list-item v-else-if="context.veranstalter" icon="user">
							{{ context.veranstalter }}
						</nk-list-item>
						<nk-list-item
							v-if="context.brancheneintrag_veranstaltungsort"
							icon="map"
						>
							<nk-link
								:link="
									context.brancheneintrag_veranstaltungsort.rubriken[0]
										.detailURL
								"
							>
								{{ context.brancheneintrag_veranstaltungsort.titel }}
							</nk-link>
							,
							{{
								context.brancheneintrag_veranstaltungsort.adresse.strasse &&
								context.brancheneintrag_veranstaltungsort.adresse.strasse.trim()
									.length
									? `${context.brancheneintrag_veranstaltungsort.adresse.strasse}`
									: null
							}},
							{{
								context.brancheneintrag_veranstaltungsort.adresse.plz &&
								context.brancheneintrag_veranstaltungsort.adresse.plz.trim()
									.length
									? `${context.brancheneintrag_veranstaltungsort.adresse.plz}`
									: null
							}}
							{{
								context.brancheneintrag_veranstaltungsort.adresse.ort &&
								context.brancheneintrag_veranstaltungsort.adresse.ort.trim()
									.length
									? `${context.brancheneintrag_veranstaltungsort.adresse.ort}`
									: null
							}}
						</nk-list-item>
						<nk-list-item v-else-if="context.veranstaltungsort" icon="map">
							{{ context.veranstaltungsort
							}}{{
								context.adresse.strasse &&
								context.adresse.strasse.trim().length &&
								context.veranstaltungsort &&
								context.veranstaltungsort.trim().length
									? ', '
									: null
							}}
							{{
								context.adresse.strasse && context.adresse.strasse.trim().length
									? `${context.adresse.strasse}`
									: null
							}}{{
								context.veranstaltungsort &&
								context.veranstaltungsort.trim().length &&
								((context.adresse.plz && context.adresse.plz.trim().length) ||
									(context.adresse.ort && context.adresse.ort.trim().length))
									? ', '
									: null
							}}
							{{
								context.adresse.plz && context.adresse.plz.trim().length
									? `${context.adresse.plz}`
									: null
							}}
							{{
								context.adresse.ort && context.adresse.ort.trim().length
									? `${context.adresse.ort}`
									: null
							}}
						</nk-list-item>
						<nk-list-item v-if="gmapLink" icon="navigation">
							<nk-link :link="gmapLink" target="_blank">Anfahrt planen</nk-link>
						</nk-list-item>
					</nk-list>
					<nk-divider display="flex" />
					<div class="nk-cms-content" v-html="context.text" />
				</nk-wrapper>
			</nk-col>
		</nk-row>
	</nk-wrapper>
</template>

<script>
import TemplatesDefault from '../../mixins/templates-default';
import {useError} from '#imports';

/**
 * @prop {object} context
 */
export default {
	name: 'NkTemplateVeranstaltungenDetail',
	mixins: [TemplatesDefault],

	head() {
		return {
			script: [
				{
					type: 'application/ld+json',
					innerHTML: JSON.stringify({
						'@context': 'http://schema.org',
						'@type': 'Event',
						name: this.context.titel,
						startDate: this.context.datum_von,
						endDate: this.context.datum_bis,
						location: {
							'@type': 'Place',
							name: this.context.brancheneintrag_veranstaltungsort
								? this.context.brancheneintrag_veranstaltungsort.titel
								: this.context.veranstaltungsort,
							address: {
								'@type': 'PostalAddress',
								streetAddress: this.context.adresse.strasse,
								postalCode: this.context.adresse.plz,
								addressLocality: this.context.adresse.ort,
							},
						},
						image: this.context.bild,
						description: this.context.text,
					}),
				},
			],
		};
	},

	computed: {
		hasImageGallery() {
			if (
				this.context.bildergalerie_zuordnung &&
				this.context.bildergalerie_zuordnung.bilder.length
			) {
				return true;
			}
			return false;
		},
		gmapLink() {
			if (
				this.context.adresse?.geokoordinaten?.latitude &&
				this.context.adresse?.geokoordinaten?.longitude
			) {
				return `https://www.google.com/maps/dir/?api=1&destination=${this.context.adresse.geokoordinaten.latitude},${this.context.adresse.geokoordinaten.longitude}`;
			}

			if (
				this.context.adresse?.strasse &&
				this.context.adresse?.plz &&
				this.context.adresse?.ort
			) {
				const adresse = `${this.context.adresse.strasse} ${this.context.adresse.ort} ${this.context.adresse.plz}`;
				return `https://www.google.com/maps/dir/?api=1&destination=${encodeURI(
					adresse,
				)}`;
			}

			if (
				this.context.brancheneintrag_veranstaltungsort &&
				this.context.brancheneintrag_veranstaltungsort.adresse.strasse &&
				this.context.brancheneintrag_veranstaltungsort.adresse.plz &&
				this.context.brancheneintrag_veranstaltungsort.adresse.ort
			) {
				const adresse = `${this.context.brancheneintrag_veranstaltungsort.adresse.strasse} ${this.context.brancheneintrag_veranstaltungsort.adresse.ort} ${this.context.brancheneintrag_veranstaltungsort.adresse.plz}`;
				return `https://www.google.com/maps/dir/?api=1&destination=${encodeURI(
					adresse,
				)}`;
			}

			return false;
		},
	},

	created() {
		if (typeof this.context !== 'object') {
			useError();
		}
	},

	methods: {
		getDateTimeString(object, dateFormat, timeFormat) {
			const nDateObject = object;
			return this.$getDateTimeString(nDateObject, dateFormat, timeFormat);
		},
	},
};
</script>
